"use client";

import confirm from "antd/es/modal/confirm";
import { ReloadOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import ModalNotificaciones from "../../modals/ModalNotificaciones";
import {
  createNotificacion,
  deleteNotificacion,
  getNotificaciones,
  sendNotification,
} from "../../services/notificacionesService";
import useAuth from "../../hooks/useAuth";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
// === Tipos de notificación ===
const TIPO_NOTIFICACION = {
  NINGUNA: "Ninguna",
  ALERTA: "Alerta",
  INFORMACION: "Información",
  EVENTO: "Evento",
  CONVOCATORIA: "Convocatoria",
  PARTIDO: "Partido",
  AUTORIZACION: "Autorización",
  "Orden de pago": "Orden de pago",
};



// === Helpers ===
function clsx(...c) {
  return c.filter(Boolean).join(" ");
}

function Badge({ children, variant = "default" }) {
  const variants = {
    default: "bg-slate-100 text-slate-700 border-slate-200",
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    danger: "bg-rose-50 text-rose-700 border-rose-200",
    info: "bg-blue-50 text-blue-700 border-blue-200",
    warning: "bg-amber-50 text-amber-700 border-amber-200",
    muted: "bg-slate-50 text-slate-500 border-slate-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
  };
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1 rounded-full border text-xs px-2 py-1",
        variants[variant]
      )}
    >
      {children}
    </span>
  );
}

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-0 grid place-items-center p-4">
        <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b">
            <h3 className="text-base font-semibold">{title}</h3>
            <button
              onClick={onClose}
              className="rounded-lg px-2 py-1 text-slate-600 hover:bg-slate-100"
            >
              ✕
            </button>
          </div>
          <div className="p-5">{children}</div>
        </div>
      </div>
    </div>
  );
}

// === Iconos por tipo ===
function getIconForType(tipo) {
  switch (tipo) {
    case TIPO_NOTIFICACION.SISTEMA:
      return "⚙️";
    case TIPO_NOTIFICACION.USUARIO:
      return "👤";
    case TIPO_NOTIFICACION.ALERTA:
      return "⚠️";
    case TIPO_NOTIFICACION.INFORMACION:
      return "ℹ️";
    case TIPO_NOTIFICACION.EVENTO:
      return "📅";
    default:
      return "🔔";
  }
}

// Formatea una fecha/hora recibida desde la API a strings legibles
function formatDateTime(src) {
  if (!src && src !== 0) return { date: "", time: "" };
  try {
    const s = String(src).trim();

    // Si viene en formato 'YYYY-MM-DD HH:mm:ss' o similar
    const parts = s.split(" ");
    let datePart = parts[0] || s;
    let timePart = parts[1] || "";

    // Si datePart es 'YYYY-MM-DD' formatear a DD/MM/YYYY
    const m = datePart.match(/(\d{4})-(\d{2})-(\d{2})/);
    let date = "";
    if (m) {
      const [, y, mo, d] = m;
      date = `${d}/${mo}/${y}`;
    } else {
      // intentar parsear con Date y formatear
      const dt = new Date(s);
      if (!isNaN(dt)) {
        date = dt.toLocaleDateString();
        // si devuelve formato con comas, limpiar
        date = date.replace(/,/g, "");
      }
    }

    // Formatear hora a HH:MM
    let time = "";
    if (timePart) {
      // tomar horas y minutos
      const tp = timePart.split(":");
      if (tp.length >= 2) {
        time = `${tp[0].padStart(2, "0")}:${tp[1].padStart(2, "0")}`;
      } else {
        time = timePart;
      }
    } else {
      // intentar obtener hora desde Date
      const dt = new Date(s.replace(" ", "T"));
      if (!isNaN(dt)) {
        const hh = String(dt.getHours()).padStart(2, "0");
        const mm = String(dt.getMinutes()).padStart(2, "0");
        time = `${hh}:${mm}`;
      }
    }

    return { date, time };
  } catch (error) {
    return { date: "", time: "" };
  }
}

// === View ===
export default function AdminNotifications() {
  const { user } = useAuth();

  const [notifications, setNotifications] = useState([]);
  const [query, setQuery] = useState("");
  const [tipo, setTipo] = useState("todos");
  const [estado, setEstado] = useState("todos");
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("cards"); // "cards" | "list"
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [formData, setFormData] = useState({
    tipo: "",
    titulo: "",
    cuerpo: "",
    grupo_id: null,
    programada_en: null,
    variables: {},
  });

  const filtered = useMemo(() => {
    return notifications.filter((n) => {
      const matchesText =
        `${n.titulo} ${n.mensaje} ${n.remitente} ${n.destinatario}`
          .toLowerCase()
          .includes((query || "").toLowerCase());
      const matchesTipo =
        tipo === "todos"
          ? true
          : String(n.tipo || "").toLowerCase() === String(tipo || "").toLowerCase();
      const matchesEstado = estado === "todos" ? true : n.estado === estado;
      return matchesText && matchesTipo && matchesEstado;
    });
  }, [notifications, query, tipo, estado]);



  function openDetail(n) {
    setSelected(n);
    setDetailOpen(true);
    // Marcar como leída al abrir

  }


  function deleteNotification(id) {
    if (confirm("¿Estás seguro de eliminar esta notificación?")) {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      setDetailOpen(false);
      setSelected(null);
    }
  }

  const handleCreateNotification = async (data) => {
    // Support multiple group ids: data.grupo_id can be an array
    const now = new Date();
    const basePayload = {
      tipo: data.tipo,
      titulo: data.titulo,
      cuerpo: data.cuerpo,
      enviada_en: now.toISOString().split("T")[0] + " " + now.toTimeString().slice(0, 5),
      programada_en: data.programada_en || null,
      variables: {
        user_institucion_id: user.user_metadata.institucionId,
        user_nombre: user.user_metadata.nombres,
        user_apellido: user.user_metadata.apellidos,
        user_email: user.email,
        user_rol: user.rol,
      },
    };

    const groupIds = Array.isArray(data.grupo_id) ? data.grupo_id : [data.grupo_id];

    try {
      setLoading(true);
      // Create one notification per selected group
      const promises = groupIds.map((gid) => {
        const payload = { ...basePayload, grupo_id: gid };
        return createNotificacion(payload);
      });
      await Promise.all(promises);

      setCreateOpen(false);
      await handleLoadNotificaciones();
      // Reset form (grupo_id as empty array)
      setFormData({
        tipo: "push",
        titulo: "",
        cuerpo: "",
        grupo_id: [],
        programada_en: null,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  /**************************************+ */
  const handleLoadNotificaciones = async () => {
    setLoading(true);
    try {
      const data = await getNotificaciones();
      setNotifications(
        data.map((n) => {
          // intento de parseo de fecha/hora si viene en formato 'YYYY-MM-DD HH:mm:ss'
          let fecha = "";
          let hora = "";
          if (n.creado_en) {
            const { date, time } = formatDateTime(n.creado_en);
            fecha = date;
            hora = time;
          }
          return {
            id: n.id,
            tipo: n.tipo || "Ninguna",
            titulo: n.titulo,
            mensaje: n.cuerpo,
            fecha,
            hora,
            remitente:
              (n.variables?.user_email || ""),
            destinatario: n.grupos?.nombre || "Todos",
          };
        })
      );
      if (data.status) {
        toast.success("Notificación creada correctamente");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  /***************************************** */
  const handleDeleteNotificacion = async (id) => {
    try {
      Swal.fire({
        title: '¿Estás seguro de eliminar esta notificación?',
        showCancelButton: true,
        confirmButtonText: 'Eliminar',
        confirmButtonColor: '#d33',
        cancelButtonText: 'Cancelar',
      }).then(async (result) => {
        if (result.isConfirmed) {
          await deleteNotificacion(id);
          await handleLoadNotificaciones();
        }
      })

    } catch (error) {
      console.error(error);
    }
  };


  const handleSendNotificacion = async (id) => {
    try {
      Swal.fire({
        title: '¿Estás seguro de enviar esta notificación?',
        showCancelButton: true,
        cancelButtonText: 'Cancelar',
        confirmButtonText: 'Enviar',
      }).then(async (result) => {
        if (result.isConfirmed) {
          const data = await sendNotification(id);
          if (data.status) {
            toast.success("Notificación enviada correctamente");
          } else if (data.error) {
            toast.error("Error al enviar la notificación");
          }
        }
      })
    } catch (error) {
      console.error(error);
    }
  }
  /*********************************** */
  useEffect(() => {
    handleLoadNotificaciones();
  }, []);

  return (
    <div className="mx-auto max-w-7xl p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl md:text-2xl font-semibold">
              Notificaciones
            </h1>

          </div>
          <p className="text-slate-600 text-sm">
            Gestiona tus notificaciones y alertas del sistema.
          </p>
        </div>
        {/* actions moved to filters row for consistent layout with other views */}
      </div>

      {/* Filtros + Acciones en la misma fila */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr,260px] gap-3 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-[1fr,180px,160px] gap-3">
          <input
            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100"
            placeholder="Buscar por título, mensaje, remitente o destinatario"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <select
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
          >
            <option value="todos">Todos los tipos</option>
            {Object.values(TIPO_NOTIFICACION).map((label) => (
              <option key={label} value={label}>{label}</option>
            ))}
          </select>

        </div>

        <div className="flex items-center justify-end gap-2">


          <div className="inline-flex items-center gap-2 rounded-xl border bg-white p-1">
            <button
              onClick={() => setViewMode("cards")}
              title="Vista de tarjetas"
              className={clsx(
                "p-1 rounded-md text-sm",
                viewMode === "cards" ? "bg-slate-100" : "hover:bg-slate-50"
              )}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode("list")}
              title="Vista de lista"
              className={clsx(
                "p-1 rounded-md text-sm",
                viewMode === "list" ? "bg-slate-100" : "hover:bg-slate-50"
              )}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h.01M4 12h.01M4 18h.01M8 6h13M8 12h13M8 18h13" />
              </svg>
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleLoadNotificaciones}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-100 text-slate-700 px-3 py-2 text-sm font-medium hover:bg-slate-200 active:scale-[.99] transition disabled:opacity-50 disabled:cursor-not-allowed"
              title="Recargar datos"
            >
              <ReloadOutlined className="text-base" spin={loading} />
            </button>
            <button
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center justify-center rounded-xl bg-blue-600 text-white px-4 py-2 text-sm hover:bg-blue-500 active:scale-[.99] transition whitespace-nowrap"
            >
              + Nueva Notificación
            </button>

          </div>
        </div>
      </div>

      {/* Grid / List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <article
              key={i}
              className="overflow-hidden rounded-2xl border bg-white shadow-sm"
            >
              <div className="p-4 border-b border-slate-100">
                <div className="h-4 w-32 bg-slate-200 rounded animate-pulse mb-2" />
                <div className="h-3 w-full bg-slate-100 rounded animate-pulse" />
              </div>
              <div className="p-4">
                <div className="h-3 w-3/4 bg-slate-100 rounded animate-pulse mb-2" />
                <div className="h-3 w-1/2 bg-slate-100 rounded animate-pulse" />
              </div>
              <div className="px-4 pb-4 pt-2">
                <div className="h-9 w-32 bg-slate-100 rounded-xl animate-pulse" />
              </div>
            </article>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🔔</div>
          <h3 className="text-lg font-semibold text-slate-700 mb-2">
            No hay notificaciones
          </h3>
          <p className="text-slate-500 text-sm">
            No se encontraron notificaciones con los filtros aplicados.
          </p>
        </div>
      ) : viewMode === "cards" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((notif) => {
            return (
              <article
                key={notif.id}
                className={clsx(
                  "relative overflow-hidden rounded-2xl border bg-white shadow-sm hover:shadow-md transition cursor-pointer",
                )}
                onClick={() => openDetail(notif)}
              >
                {/* Header con icono y badges */}
                <div className="p-4 border-b border-slate-100">
                  <div className="flex items-start gap-3">
                    <div className="text-3xl flex-shrink-0">{getIconForType(notif.tipo)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge
                          variant={
                            notif.tipo === TIPO_NOTIFICACION.ALERTA
                              ? "danger"
                              : notif.tipo === TIPO_NOTIFICACION.EVENTO
                                ? "info"
                                : notif.tipo === TIPO_NOTIFICACION.USUARIO
                                  ? "purple"
                                  : "muted"
                          }
                        >
                          {notif.tipo}
                        </Badge>

                      </div>
                      <h3 className="text-base font-semibold line-clamp-2 mb-1">{notif.titulo}</h3>
                      <p className="text-sm text-slate-600 line-clamp-2">{notif.mensaje}</p>
                    </div>
                  </div>

                  {/* Actions in top-right (match other views) */}
                  <div className="absolute right-3 top-3 inline-flex items-center gap-2">

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteNotificacion(notif.id);
                      }}
                      title="Eliminar"
                      className="rounded-md bg-white border border-rose-200 text-rose-600 p-1 shadow-sm hover:bg-rose-50"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSendNotificacion(notif.id);
                      }}
                      title="Enviar"
                      className="rounded-md bg-white border border-rose-200 text-blue-600 p-1 shadow-sm hover:bg-rose-50"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14L21 3m0 0l-6.5 18a.55.55 0 0 1-1 0L10 14l-7-3.5a.55.55 0 0 1 0-1z" /></svg>
                    </button>
                  </div>
                </div>

                {/* Body con información */}
                <div className="p-4">
                  <div className="text-sm text-slate-700 grid grid-cols-2 gap-y-2 gap-x-3">
                    <span className="text-slate-500">Fecha:</span>
                    <span>{notif.fecha} · {notif.hora}</span>
                    <span className="text-slate-500">Remitente:</span>
                    <span className="truncate" title={notif.remitente}>{notif.remitente}</span>
                    <span className="text-slate-500">Destinatario:</span>
                    <span className="truncate" title={notif.destinatario}>{notif.destinatario}</span>
                  </div>
                </div>

              </article>
            );
          })}
        </div>
      ) : (
        // List / Table view
        <div className="rounded-xl border bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left px-4 py-3">Tipo</th>
                <th className="text-left px-4 py-3">Título</th>
                <th className="text-left px-4 py-3">Remitente</th>
                <th className="text-left px-4 py-3">Destinatario</th>
                <th className="text-left px-4 py-3">Fecha</th>
                <th className="text-right px-4 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((notif) => (
                <tr key={notif.id} className="border-b hover:bg-slate-50">
                  <td className="px-4 py-3 flex items-center gap-2">
                    <span className="text-lg">{getIconForType(notif.tipo)}</span>
                    <span className="text-slate-700">{notif.tipo}</span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => openDetail(notif)}
                      className="text-slate-700 hover:underline text-left"
                    >
                      {notif.titulo}
                    </button>
                  </td>
                  <td className="px-4 py-3">{notif.remitente}</td>
                  <td className="px-4 py-3">{notif.destinatario}</td>
                  <td className="px-4 py-3">{notif.fecha} {notif.hora}</td>

                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2">

                      <button
                        onClick={() => handleSendNotificacion(notif.id)}
                        className="rounded-xl border border-blue-600 text-blue-600 px-3 py-1 text-sm hover:bg-rose-50"
                      >
                        Enviar
                      </button>
                      <button
                        onClick={() => handleDeleteNotificacion(notif.id)}
                        className="rounded-xl border border-rose-600 text-rose-600 px-3 py-1 text-sm hover:bg-rose-50"
                      >
                        Eliminar
                      </button>

                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      <Modal
        open={detailOpen && !!selected}
        onClose={() => {
          setDetailOpen(false);
          setSelected(null);
        }}
        title="Detalle de notificación"
      >
        {selected && (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="text-4xl">{getIconForType(selected.tipo)}</div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold mb-2">
                  {selected.titulo}
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  {selected.mensaje}
                </p>
              </div>
            </div>

            <div className="border-t pt-4">
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <dt className="text-slate-500 font-medium">Fecha y hora:</dt>
                <dd className="text-slate-700">
                  {selected.fecha} a las {selected.hora}
                </dd>
                <dt className="text-slate-500 font-medium">Remitente:</dt>
                <dd className="text-slate-700">{selected.remitente}</dd>
                <dt className="text-slate-500 font-medium">Destinatario:</dt>
                <dd className="text-slate-700">{selected.destinatario}</dd>
              </dl>
            </div>

            {/* Información adicional si existe en selected.extra_info */}
            {selected.extra_info && Array.isArray(selected.extra_info) && selected.extra_info.length > 0 && (
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-slate-700 mb-2">Información adicional</h4>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  {selected.extra_info.map((it, idx) => (
                    <div key={idx}>
                      <dt className="text-slate-500 font-medium">{it.label}</dt>
                      <dd className="text-slate-700">{it.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 pt-2">

              <button
                onClick={() => deleteNotification(selected.id)}
                className={clsx(
                  "w-full inline-flex items-center justify-center rounded-xl border border-rose-600 text-rose-600 px-3 py-2 text-sm hover:bg-rose-50 hover:border-rose-400 hover:text-rose-500 active:scale-[.99] transition",
                )}
              >
                Eliminar notificación
              </button>
            </div>
          </div>
        )}
      </Modal>
      <ModalNotificaciones
        createOpen={createOpen}
        setCreateOpen={setCreateOpen}
        formData={formData}
        setFormData={setFormData}
        handleCreateNotification={handleCreateNotification}
      />
    </div>
  );
}
